import { DocumentNode, FetchResult, gql, useMutation } from '@apollo/client';
import { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { ContactInfo, ContactInput, ManageContactInput, Person, SaveBusinessContactsMutation } from 'sr-types/lib/person/v1/graphql';
import { Icons } from '../../../common/icons/Icons';
import { UserContext } from '../../../common/auth/UserContext';
import { client, useGetPersonContactInfo } from '../../../common/components/person/person';
import { PROFILES } from '../../../common/nav/apps';
import { failedToastMessage, successToastMessage } from '../../../common/utils/commonUtils';
import useHistory from '../../../common/utils/useHistory';

export type BusinessContactsEditorProps = {
    personEditingDetails: ContactInput;
    onCloseEditor: () => void;
    directAddContact?: boolean;
    setPersonData: Dispatch<SetStateAction<Map<string, string>>>;
    setShowPersonEditor: Dispatch<SetStateAction<boolean>>;
    initialState: ContactInput;
};

export const saveBusinessContact: DocumentNode = gql`
    mutation SaveBusinessContact($personId: ID!, $input: [ManageContactInput]!) {
        saveBusinessContacts(personId: $personId, input: $input) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const useSaveBusinessContacts = () => {
    const { activeOrganizationAccount } = useContext(UserContext);

    const [save, { loading: isBusinessContactsSaving }] = useMutation(saveBusinessContact, {
        client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });

    const saveBusinessContacts = (personId: string, input: ManageContactInput[]) => {
        return new Promise<FetchResult<SaveBusinessContactsMutation>>((resolve, reject) => {
            save({
                variables: {
                    personId,
                    input
                }
            })
                .then((res) => resolve(res))
                .catch((err) => reject(err));
        });
    };

    return { saveBusinessContacts, isBusinessContactsSaving };
};

export const deleteBusinessContact: DocumentNode = gql`
    mutation DeleteBusinessContact($personId: ID!, $input: ContactInput) {
        deleteBusinessContact(personId: $personId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const useDeleteBusinessContact = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [removeBusinessContact, { loading: removingBusinessContact }] = useMutation(deleteBusinessContact, {
        client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });

    const directDeleteBusinessContact = (personId: string, input: ContactInput) => {
        const { profileImage, ...contact } = input as Person;
        return new Promise((resolve, reject) => {
            removeBusinessContact({
                variables: {
                    personId,
                    input: { ...contact, profileImageReference: { id: profileImage || '' } }
                }
            })
                .then((res) => handleResponseForDeletingBusinessContactsDirectly(res, resolve, reject))
                .catch((err) => {
                    failedToastMessage('business.contact.direct.remove.error');
                    reject(err);
                });
        });
    };

    return { directDeleteBusinessContact, removingBusinessContact };
};

const handleResponseForDeletingBusinessContactsDirectly = (res, resolve, reject) => {
    if (res.data.deleteBusinessContact.id) {
        successToastMessage('business.contact.direct.remove.success');
        resolve(res);
    } else {
        failedToastMessage('business.contact.direct.remove.error');
        reject(res.data.deleteBusinessContact.errors);
    }
};

export const useGetPersonId = () => {
    const { application, userProfile } = useContext(UserContext);
    const { routeParams } = useHistory();
    const { entityId } = routeParams;

    const isUserProfile = application.id === PROFILES.id;
    const personId = useMemo(
        () => (isUserProfile ? userProfile.personReference.id : entityId),
        [entityId, isUserProfile, userProfile.personReference.id]
    );

    return { personId };
};

export const useDirectlyAddBusinessContact = () => {
    const { personId } = useGetPersonId();
    const { saveBusinessContacts, isBusinessContactsSaving } = useSaveBusinessContacts();
    const { getContactInfo, contactInfoLoading: loadingContactInfoDirectly } = useGetPersonContactInfo();

    const directlyAddBusinessContact = (contact: ContactInput, businessContacts: ContactInput[]) => {
        const existing = businessContacts?.find((c) => contact.personReference.id === c.personReference.id);

        return new Promise((resolve, reject) => {
            const { profileImage, ...contactInput } = contact as Person;

            handleDeleteContact({
                getContactInfo,
                contact,
                saveBusinessContacts,
                personId,
                existing,
                businessContacts,
                contactInput,
                resolve,
                reject
            });
        });
    };

    return { directlyAddBusinessContact, isBusinessContactsSaving, loadingContactInfoDirectly };
};

const handleDeleteContact = async ({
    getContactInfo,
    contact,
    saveBusinessContacts,
    personId,
    existing,
    businessContacts,
    contactInput,
    resolve,
    reject
}) => {
    let newContact = null;
    const updatingSamePerson = existing?.personReference?.id === contact.personReference.id;
    if (!existing || !updatingSamePerson) {
        newContact = await fetchContactInfo({ getContactInfo, contact });
    }
    //as Person, as ContactInput flags are for the typing purposes. ContactInput and Person are overlaping but also have some different fields.
    //This should not introuduce issues
    const { profileImage, ...existingContact } = (existing as Person) ?? {};

    saveBusinessContacts(personId, [
        {
            existing: existingContact as ContactInput,
            new: updatingSamePerson
                ? {
                      ...contactInput,
                      profileImageReference: { id: profileImage || '' }
                  }
                : newContact
        }
    ])
        .then((data: any) => {
            handleErrorsForSavingBusinessContactsDirectly(data?.saveBusinessContacts?.errors);
            resolve(data);
        })
        .catch((err) => {
            failedToastMessage('business.contact.save.directly.error');
            reject(err);
        })
        .finally(() =>
            handlePersonBusinessContactsStateChange(
                businessContacts,
                updatingSamePerson ? { ...contact } : newContact,
                existing?.personReference?.id
            )
        );
};

const fetchContactInfo = async ({ getContactInfo, contact }) => {
    let newContact = null;
    await getContactInfo(contact.personReference.id).then((contactInfo: Person) => {
        const { profileImage, ...contactInput } = contactInfo;
        newContact = {
            ...(contactInput as ContactInput),
            ...getBusinessContactFields(contact),
            profileImageReference: { id: profileImage || '' }
        };
    });
    return newContact;
};

export const stripUndefinedFields = <T extends Record<string, unknown>>(value: T): Partial<T> => {
    return Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)) as Partial<T>;
};

type BusinessContactFields = Pick<
    ContactInput,
    'personReference' | 'role' | 'isPrimary' | 'organization' | 'organizationReference'
>;

export const getBusinessContactFields = (contact: ContactInput): Partial<BusinessContactFields> => {
    const { personReference, role, isPrimary, organization, organizationReference } = contact;
    const organizationName =
        organizationReference?.label?.trim() ||
        (typeof organization === 'string' ? organization.trim() : undefined);

    return stripUndefinedFields({
        personReference,
        role,
        isPrimary,
        organization: organizationName,
        organizationReference
    });
};

const handleErrorsForSavingBusinessContactsDirectly = (errors) => {
    errors
        ? failedToastMessage('business.contact.save.directly.error')
        : successToastMessage('business.contact.save.directly.success');
};

export const editPersonBusinessContacts = (
    contactState: ContactInput,
    personEditingDetails: ContactInput,
    getContactInfo: (id: string) => Promise<ContactInput>,
    businessContacts: ContactInput[],
    onClose: () => void
) => {
    const personState = getBusinessContactFields(contactState);

    const existingContactId = personEditingDetails?.personReference?.id;
    const isDifferentPerson = existingContactId !== contactState?.personReference?.id;

    if (Object.keys(personEditingDetails || {}).length === 0 || isDifferentPerson) {
        getContactInfo(contactState.personReference.id)
            .then((contactInfo) => {
                handlePersonBusinessContactsStateChange(
                    businessContacts,
                    {
                        ...(contactInfo as ContactInput),
                        ...personState
                    },
                    existingContactId
                );
                successToastMessage(`business.contact.${personEditingDetails ? 'edit' : 'add'}.success.message`);
            })
            .catch(() => failedToastMessage('business.contact.add.failed.message'))
            .finally(() => onClose());
    } else {
        handlePersonBusinessContactsStateChange(
            businessContacts,
            {
                ...personEditingDetails,
                ...personState
            },
            existingContactId
        );
        onClose();
        successToastMessage(`business.contact.${personEditingDetails ? 'edit' : 'add'}.success.message`);
    }
};

export const handlePersonBusinessContactsStateChange = (
    businessContacts: ContactInput[],
    contact: ContactInput,
    existingContactId: string = null
) => {
    if (contact.isPrimary) {
        rewriteCurrentPrimaryContact(businessContacts);
    }

    if (existingContactId) {
        const contactToEdit = businessContacts.find((contact) => contact.personReference.id === existingContactId);
        const previousPrimaryState = contactToEdit.isPrimary;
        Object.assign(contactToEdit, contact);

        if (contact.isPrimary && businessContacts.length > 1) {
            makePrimaryContactFirstElement(businessContacts, contactToEdit.personReference.id);
        } else if (previousPrimaryState && !contact.isPrimary) {
            sortBusinessContactsByName(businessContacts);
        }
    } else {
        if (contact.isPrimary) {
            if (businessContacts.length > 1) {
                sortBusinessContactsByName(businessContacts);
            }
            businessContacts.unshift(contact);
        } else {
            businessContacts.push(contact);
            if (businessContacts.length > 1 && businessContacts[0].isPrimary) {
                makePrimaryContactFirstElement(businessContacts, businessContacts[0].personReference.id);
            } else {
                sortBusinessContactsByName(businessContacts);
            }
        }
    }
};

const makePrimaryContactFirstElement = (businessContacts: ContactInput[], existingContactId: string) => {
    const contactIndex = businessContacts.findIndex((contact) => contact.personReference.id === existingContactId);
    const [removed] = businessContacts.splice(contactIndex, 1);
    sortBusinessContactsByName(businessContacts);
    businessContacts.unshift(removed);
};

const sortBusinessContactsByName = (businessContacts: ContactInput[]) => {
    businessContacts.sort((a, b) => {
        const firstNameComparison = a.name.firstName.localeCompare(b.name.firstName);
        return firstNameComparison !== 0 ? firstNameComparison : a.name.firstName.localeCompare(b.name.firstName);
    });
};

export const rewriteCurrentPrimaryContact = (businessContacts: ContactInput[]) => {
    for (const contact of businessContacts) {
        if (contact.isPrimary) {
            contact.isPrimary = false;
            break;
        }
    }
};

export const getAddedBusinessContactsIds = (businessContacts: ContactInput[], currentPersonId: string) => {
    const addedIds = businessContacts?.map((contact) => contact?.personReference?.id) || [];
    addedIds.push(currentPersonId);
    return addedIds;
};

export const removeBusinessContact = (businessContacts: ContactInput[], contactToDeleteId: string) => {
    const updatedBusinessContact = businessContacts.filter((c) => c.personReference.id !== contactToDeleteId);
    return updatedBusinessContact;
};

export const getContactInfoForPreview = (contactInfo: ContactInfo | undefined, businessContacts: ContactInput[]) => {
    const primaryContact = businessContacts?.find((contact) => contact.isPrimary);
    const phoneNumber = primaryContact ? primaryContact?.contactInfo?.phone?.number || '' : contactInfo?.phone?.number;
    const emailAddress = primaryContact
        ? primaryContact?.contactInfo?.email?.address || ''
        : contactInfo?.email?.address;

    const emailIcon = primaryContact ? Icons.PrimaryContactEmail : Icons.Email;
    const phoneIcon = primaryContact ? Icons.PrimaryContactPhone : Icons.PhoneOutlined;

    return { phoneNumber, emailAddress, emailIcon, phoneIcon };
};
